import {
  Role,
  useGetRichiestaPreventivoById,
  useGetUsers,
  useUpdateRichiestaPreventivoUserCommerciale,
} from "@/api";
import Box from "@/elements/Box";
import Button from "@/elements/Button";
import Typography from "@/elements/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  RichiestaPreventivoDetailContent,
} from "../RichiestaPreventivoDetailDialog/RichiestaPreventivoDetailContent";
import { downloadRichiestaPdf } from "../RichiestaPreventivoDetailDialog";
import Loading from "../Loading";
import LoadingError from "../LoadingError";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

function RichiestaPreventivoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();


  const { isLoading, error, data, refetch } = useGetRichiestaPreventivoById(
    Number(id),
    { query: { enabled: !!id } }
  );
  const { data: usersData } = useGetUsers({ role: Role.Commerciale });
  const { mutateAsync: updateUserCommerciale, isLoading: isUpdating } =
    useUpdateRichiestaPreventivoUserCommerciale();

  const richiesta = data?.data;
  const users = usersData?.data ?? [];

  const [selectedCommercialeId, setSelectedCommercialeId] = useState<
    number | ""
  >("");

  useEffect(() => {
    setSelectedCommercialeId(richiesta?.userCommercialeId ?? "");
  }, [richiesta?.userCommercialeId]);

  const handleDownloadPdf = async () => {
    if (!richiesta) return;
    try {
      await downloadRichiestaPdf(
        richiesta,
        t("richiestaPreventivoDetailPage.filePdfDefault")
      );
    } catch (err) {
      console.error(t("richiestaPreventivoDetailPage.errori.downloadPdf"), err);
    }
  };

  const handleCommercialeChange = async (value: number | "") => {
    if (!richiesta) return;
    setSelectedCommercialeId(value);
    try {
      await updateUserCommerciale({
        id: richiesta.id,
        data: { userCommercialeId: value !== "" ? value : null },
      });
      await refetch();
      toast.success(t("richiestaPreventivoDetailPage.toast.commercialeAggiornato"));
    } catch {
      toast.error(
        t("richiestaPreventivoDetailPage.errori.aggiornaCommerciale")
      );
      setSelectedCommercialeId(richiesta.userCommercialeId ?? "");
    }
  };

  if (isLoading) {
    return <Loading />;
  }
  if (error || !richiesta) {
    return <LoadingError />;
  }

  return (
    <Box p={2}>
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        mb={3}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => navigate("/richieste_risparmi")}
            startIcon={<ArrowBackIcon />}
          >
            {t("richiestaPreventivoDetailPage.azioni.tornaAlleRichieste")}
          </Button>
          <Typography variant="h5" ml={1}>
            {richiesta.nomeAzienda
              ? `${richiesta.nomeAzienda} — ${richiesta.nomeRichiedente ?? ""}`
              : t("richiestaPreventivoDetailPage.titolo")}
          </Typography>
        </Box>

        <Button
          variant="contained"
          color="primary"
          onClick={handleDownloadPdf}
          startIcon={<DownloadIcon />}
          disabled={!richiesta.filePdf}
        >
          {t("richiestaPreventivoDetailPage.azioni.scaricaPdf")}
        </Button>
      </Box>

      {/* Assegnazione commerciale */}
      <Box mb={2} maxWidth={300}>
        <FormControl fullWidth size="small" disabled={isUpdating}>
          <InputLabel>
            {t("richiestaPreventivoDetailPage.campi.commercialeAssegnato")}
          </InputLabel>
          <Select
            label={t("richiestaPreventivoDetailPage.campi.commercialeAssegnato")}
            value={selectedCommercialeId}
            onChange={(e) =>
              handleCommercialeChange(e.target.value as number | "")
            }
          >
            <MenuItem value="">
              {t("richiestaPreventivoDetailPage.campi.commercialeNessuno")}
            </MenuItem>
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {`${u.name ?? ""} ${u.lastName ?? ""}`.trim() || u.email}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Content */}
      <Paper elevation={2} sx={{ p: 3, maxWidth: 700 }}>
        <RichiestaPreventivoDetailContent richiesta={richiesta} />
      </Paper>
    </Box>
  );
}

export default RichiestaPreventivoDetailPage;
